/**
 * Маршруты и ворота сессии.
 *
 * Бренд стоит в URL (`/b/:brand/...`), потому что он — часть адреса рабочего места, а не
 * состояние в памяти: ссылку на конкретный черновик надо уметь переслать. Брендов на установке
 * несколько (docs/multi-brand.md): переключатель — по нажатию на знак в шапке
 * (`components/BrandSwitcher.tsx`), тема и знак бренда — от провайдера (`brand.tsx`), который
 * стоит над маршрутами и читает бренд из того же адреса.
 */

import type { JSX } from 'react';
import { Redirect, Route, Switch, useLocation } from 'wouter';
import { BrandProvider, brandOfPath, rootBrand, useBrands } from './brand.tsx';
import { BatchPage } from './routes/BatchPage.tsx';
import { ComparePage } from './routes/ComparePage.tsx';
import { LoginPage } from './routes/LoginPage.tsx';
import { MonitoringPage } from './routes/MonitoringPage.tsx';
import { PlanPage } from './routes/PlanPage.tsx';
import { StylesPage } from './routes/StylesPage.tsx';
import { WorkspacePage } from './routes/WorkspacePage.tsx';
import { useSession } from './session.tsx';

/**
 * Куда вести с корня: последний открытый в этом браузере бренд, если он ещё есть в
 * конфигурации, иначе первый из списка бэкенда (он отсортирован по `ui.order`). Хардкод
 * 'geoboost' был бы вторым источником правды о составе брендов — их список знает только бэкенд.
 */
function RootRedirect(): JSX.Element {
  const brands = useBrands();
  if (brands.error !== null) {
    return (
      <div className="app" style={{ padding: 32 }}>
        <div className="val val-err">Бэкенд не отвечает: {brands.error}</div>
      </div>
    );
  }
  if (brands.list === undefined) return <div className="app" />;
  const target = rootBrand(brands);
  if (target === null) {
    // Пустой список — не «загружается»: бэкенд ответил, что брендов нет. Белый экран навсегда
    // читался бы как зависшая загрузка.
    return (
      <div className="app" style={{ padding: 32 }}>
        <div className="val val-err">
          Бэкенд не отдал ни одного бренда — конфиги брендов не загрузились. Смотрите журнал
          бэкенда.
        </div>
      </div>
    );
  }
  return <Redirect to={`/b/${target}/backlog`} />;
}

export function App(): JSX.Element {
  const { user, ready } = useSession();
  return (
    <BrandProvider enabled={ready && user !== null}>
      <Gate />
    </BrandProvider>
  );
}

function Gate(): JSX.Element {
  const { user, ready } = useSession();
  const [location] = useLocation();

  // Пока сервер не ответил, кто мы, не рисуем ничего: иначе форма входа мигает на каждом
  // обновлении страницы у человека с живой сессией.
  if (!ready) return <div className="app" />;

  if (user === null) {
    return location === '/login' ? <LoginPage /> : <Redirect to="/login" />;
  }

  // Ключ — бренд из адреса: смена бренда ПЕРЕМОНТИРУЕТ экран вместе с шапкой. Без ключа
  // wouter переиспользует тот же экземпляр страницы для /b/geoboost/plan и /b/agi-boardroom/plan,
  // и состояние бренда A (перенос поста в календаре, открытый диалог генерации, подтверждение
  // режима, ход прогона в шапке) жило бы на экране бренда B и отправляло запросы про пост A
  // (docs/multi-brand.md §6). Внутри одного бренда ключ не меняется — переходы по вкладкам и
  // черновикам по-прежнему не теряют состояние.
  return (
    <Switch key={brandOfPath(location) ?? ''}>
      <Route path="/login">
        <Redirect to="/" />
      </Route>
      <Route path="/" component={RootRedirect} />
      <Route path="/b/:brand/backlog">
        {(p) => <WorkspacePage brand={p.brand} draftId={null} />}
      </Route>
      <Route path="/b/:brand/draft/:id">
        {(p) => <WorkspacePage brand={p.brand} draftId={p.id} />}
      </Route>
      <Route path="/b/:brand/draft/:id/v/:n">
        {(p) => <WorkspacePage brand={p.brand} draftId={p.id} versionNo={Number(p.n)} />}
      </Route>
      <Route path="/b/:brand/batch">{(p) => <BatchPage brand={p.brand} />}</Route>
      <Route path="/b/:brand/plan">{(p) => <PlanPage brand={p.brand} />}</Route>
      <Route path="/b/:brand/monitoring">{(p) => <MonitoringPage brand={p.brand} />}</Route>
      {/*
        Редактор стилей и сравнение — страницы, а не вкладки шапки (docs/release-2026-09-19.md
        §5.4, §5.6): в них приходят из «Настроек» и из готовой заявки, и адрес держит то, что
        открыто, — версию стиля или пару черновиков, — чтобы её можно было держать в соседней
        вкладке браузера и пересылать. Версия — необязательный сегмент ОДНОГО маршрута: переход
        между версиями не пересоздаёт страницу и не теряет выбранный файл и фильтр.
      */}
      <Route path="/b/:brand/styles/:versionId?">
        {(p) => <StylesPage brand={p.brand} versionId={p.versionId ?? null} />}
      </Route>
      <Route path="/b/:brand/compare/:requestId">
        {(p) => <ComparePage brand={p.brand} requestId={p.requestId} />}
      </Route>
      <Route>
        <div className="app" style={{ padding: 32 }}>
          <div className="val val-warn">Такого экрана нет.</div>
        </div>
      </Route>
    </Switch>
  );
}
